/**
 * screens/MyTransactions — Custodian
 * ──────────────────────────────────────────────────────────────────────────
 * Everything you have recorded, newest first.
 *
 * The subtitle states plainly that nothing here can be edited. That is not a
 * limitation to apologise for — it is the ledger's guarantee, and a custodian
 * who understands it will look for the Reverse action instead of hunting for an
 * edit button that does not exist.
 *
 * Grid from the design: minmax(0,1.4fr) minmax(0,1.6fr) minmax(0,1fr)
 * minmax(0,1fr).
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  Card,
  DataTable,
  EmptyPanel,
  Loading,
  Money,
  PageContainer,
  PageHeader,
  type Column,
} from '../../components';
import { neutral } from '../../theme/colors';
import { mono, numeric, sans, size as fontSize } from '../../theme/typography';
import { ellipsisStyle } from '../../theme/webStyle';
import { useAsyncData } from '../../lib/useAsyncData';
import { dateTime } from '../../lib/dates';
import { dotted } from '../../lib/format';
import { txnDirection, txnLabel } from '../../lib/status';
import { fetchMyTransactions } from '../../services/cash/cash.service';
import type { Transaction } from '../../schemas/cash';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';

export function MyTransactionsScreen() {
  const { dataVersion } = useShell();
  const { categories } = useConfig();

  const load = useCallback(() => fetchMyTransactions(), []);
  const { data, loading } = useAsyncData(load, [], {
    version: dataVersion,
    errorTitle: 'Could not load your transactions',
  });

  const rows = data ?? [];

  const categoryCode = useCallback(
    (id: string | null) => categories.find((category) => category.id === id)?.code ?? null,
    [categories],
  );

  const columns = useMemo<Column<Transaction>[]>(
    () => [
      {
        key: 'type',
        header: 'Type',
        track: { fr: 1.4 },
        render: (txn) => (
          <Text numberOfLines={1} style={styles.type}>
            {txnLabel(txn.txn_type)}
            {txn.is_reversal && <Text style={styles.reversalTag}> · reversal</Text>}
          </Text>
        ),
      },
      {
        key: 'what',
        header: 'What it was',
        track: { fr: 1.6 },
        render: (txn) => (
          <Text numberOfLines={2} style={styles.what}>
            {dotted(categoryCode(txn.category_id), txn.memo) || '—'}
          </Text>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        track: { fr: 1 },
        align: 'right',
        render: (txn) => {
          const direction = txnDirection(txn.txn_type, txn.is_reversal);
          return (
            <Money
              value={txn.amount_minor}
              cur={txn.cur}
              direction={direction}
              align="right"
              color={direction === 'in' ? neutral.ink : neutral.grey600}
            />
          );
        },
      },
      {
        key: 'when',
        header: 'When',
        track: { fr: 1 },
        render: (txn) => <Text style={styles.when}>{dateTime(txn.occurred_at)}</Text>,
      },
    ],
    [categoryCode],
  );

  if (loading && !data) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  return (
    <PageContainer testID="screen-myledger">
      <PageHeader
        title="My transactions"
        subtitle="Everything you have recorded, newest first. Nothing here can be edited — a correction is a reversal."
      />

      {rows.length === 0 ? (
        <EmptyPanel
          title="Nothing recorded yet"
          hint="Spends, collections and deposits you record will be listed here."
        />
      ) : (
        <Card>
          {/* No onRowPress: the ledger is a record, not a set of links. */}
          <View>
            <DataTable
              columns={columns}
              rows={rows}
              keyExtractor={(txn) => txn.id}
              density="roomy"
              columnGap={16}
            />
          </View>
        </Card>
      )}
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  type: {
    ...sans('500'),
    fontSize: fontSize.body,
    color: neutral.ink,
    textTransform: 'capitalize',
    ...ellipsisStyle,
  },
  reversalTag: { ...sans('400'), color: neutral.grey400 },
  what: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  when: { ...mono('400'), fontSize: fontSize.meta, ...numeric, color: neutral.grey400 },
});

export default MyTransactionsScreen;
